import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Banknote, CheckCircle, Clock, Package, Truck,
  FileText, Calendar, User, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PurchaseDetailPage = () => {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState(null);
  const [constants, setConstants] = useState({ payment_methods: [] });
  const [loading, setLoading] = useState(true);

  // Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Payment form
  const [paymentData, setPaymentData] = useState({
    monto: '', metodo_pago: 'transferencia', fecha_pago: '', referencia: '', notas: ''
  });

  // Fetch purchase
  const fetchPurchase = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/purchases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchase(response.data);
    } catch (error) {
      console.error('Error fetching purchase:', error);
      toast.error('Error al cargar la compra');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  const fetchConstants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/purchases/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    }
  }, []);

  useEffect(() => {
    fetchPurchase();
    fetchConstants();
  }, [fetchPurchase, fetchConstants]);

  // Open payment modal
  const openPaymentModal = () => {
    setPaymentData({
      monto: purchase.total,
      metodo_pago: 'transferencia',
      fecha_pago: new Date().toISOString().split('T')[0],
      referencia: '',
      notas: ''
    });
    setShowPaymentModal(true);
  };

  // Process payment
  const handlePayment = async () => {
    try {
      await axios.post(`${API}/purchases/pagar`, {
        compra_id: id,
        monto: parseFloat(paymentData.monto),
        metodo_pago: paymentData.metodo_pago,
        fecha_pago: paymentData.fecha_pago || null,
        referencia: paymentData.referencia || null,
        notas: paymentData.notas || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pago registrado exitosamente');
      setShowPaymentModal(false);
      fetchPurchase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  // Confirm draft
  const handleConfirm = async () => {
    try {
      await axios.post(`${API}/purchases/${id}/confirmar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Compra confirmada');
      setShowConfirmModal(false);
      fetchPurchase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar');
    }
  };

  // Mark as received
  const handleMarkReceived = async () => {
    try {
      await axios.post(`${API}/purchases/${id}/recibir`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marcada como recibida');
      fetchPurchase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const formatCurrency = (value) => `Q ${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const getStatusBadge = () => {
    if (!purchase) return null;
    if (purchase.estado_pago === 'pagada') {
      return <Badge className="bg-green-100 text-green-700 border-green-300 text-lg px-4 py-1">PAGADA</Badge>;
    }
    if (purchase.estado_pago === 'vencida') {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-300 text-lg px-4 py-1">
          <AlertTriangle className="w-4 h-4 mr-1" />
          VENCIDA ({purchase.dias_vencido} días)
        </Badge>
      );
    }
    return <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-lg px-4 py-1">PENDIENTE</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="text-center py-12 text-slate-500">Cargando...</div>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="text-center py-12 text-slate-500">Compra no encontrada</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/compras')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl text-slate-900">{purchase.numero_compra}</h1>
              {purchase.es_borrador && (
                <Badge variant="outline" className="text-slate-500">Borrador</Badge>
              )}
            </div>
            <p className="text-slate-500 text-sm">{purchase.proveedor_nombre}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge()}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* General Info */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Información General
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Proveedor:</span>
                  <div className="font-medium">{purchase.proveedor_nombre}</div>
                  {purchase.proveedor_nit && (
                    <div className="text-slate-500">NIT: {purchase.proveedor_nit}</div>
                  )}
                </div>
                <div>
                  <span className="text-slate-500">No. Factura Proveedor:</span>
                  <div className="font-medium">{purchase.numero_factura || '-'}</div>
                </div>
                <div>
                  <span className="text-slate-500">Fecha de Compra:</span>
                  <div className="font-medium">{purchase.fecha_compra}</div>
                </div>
                <div>
                  <span className="text-slate-500">Fecha de Recepción:</span>
                  <div className="font-medium">{purchase.fecha_recepcion || '-'}</div>
                </div>
                <div>
                  <span className="text-slate-500">Condición de Pago:</span>
                  <Badge variant="outline" className="ml-2">{purchase.condicion_pago_label}</Badge>
                </div>
                <div>
                  <span className="text-slate-500">Fecha Vencimiento:</span>
                  <div className={`font-medium ${purchase.estado_pago === 'vencida' ? 'text-red-700' : ''}`}>
                    {purchase.fecha_vencimiento_pago || 'N/A'}
                  </div>
                </div>
              </div>
              {purchase.notas && (
                <div className="mt-4 pt-4 border-t">
                  <span className="text-slate-500 text-sm">Notas:</span>
                  <p className="text-slate-700">{purchase.notas}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lines */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Detalle de Productos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4">Código</th>
                    <th className="text-left p-4">Producto</th>
                    <th className="text-right p-4">Cantidad</th>
                    <th className="text-right p-4">Costo Unit.</th>
                    <th className="text-right p-4">Subtotal</th>
                    <th className="text-center p-4">Recibido</th>
                  </tr>
                </thead>
                <tbody>
                  {purchase.lineas.map((linea, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-4 font-mono text-slate-500">{linea.producto_codigo}</td>
                      <td className="p-4">{linea.producto_nombre}</td>
                      <td className="p-4 text-right">{linea.cantidad}</td>
                      <td className="p-4 text-right">{formatCurrency(linea.costo_unitario)}</td>
                      <td className="p-4 text-right font-medium">{formatCurrency(linea.subtotal_linea)}</td>
                      <td className="p-4 text-center">
                        {linea.recibida ? (
                          <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <Clock className="w-5 h-5 text-slate-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="border-b">
                    <td colSpan={4} className="p-4 text-right">Subtotal:</td>
                    <td className="p-4 text-right font-medium">{formatCurrency(purchase.subtotal)}</td>
                    <td></td>
                  </tr>
                  {purchase.descuento > 0 && (
                    <tr className="border-b">
                      <td colSpan={4} className="p-4 text-right text-slate-500">Descuento:</td>
                      <td className="p-4 text-right text-slate-500">-{formatCurrency(purchase.descuento)}</td>
                      <td></td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={4} className="p-4 text-right text-lg font-bold">Total:</td>
                    <td className="p-4 text-right text-lg font-bold text-green-700">{formatCurrency(purchase.total)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Historial</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Created */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Compra registrada</div>
                    <div className="text-sm text-slate-500">
                      {purchase.fecha_registro} por {purchase.registrado_por_nombre || 'Sistema'}
                    </div>
                  </div>
                </div>

                {/* Reception */}
                {purchase.estado_recepcion !== 'pendiente' && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <Truck className="w-4 h-4 text-teal-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">
                        {purchase.estado_recepcion === 'recibida_completa' ? 'Recibida completa' : 'Recibida parcialmente'}
                      </div>
                      <div className="text-sm text-slate-500">{purchase.fecha_recepcion}</div>
                    </div>
                  </div>
                )}

                {/* Payment */}
                {purchase.fecha_pago && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Banknote className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">Pago registrado</div>
                      <div className="text-sm text-slate-500">
                        {purchase.fecha_pago} por {purchase.pagada_por_nombre || 'Sistema'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status Card */}
          <Card className={
            purchase.estado_pago === 'pagada' ? 'border-green-200 bg-green-50' :
            purchase.estado_pago === 'vencida' ? 'border-red-200 bg-red-50' :
            'border-amber-200 bg-amber-50'
          }>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-sm text-slate-500 mb-2">Estado de Pago</div>
                {getStatusBadge()}
                {purchase.estado_pago !== 'pagada' && purchase.fecha_vencimiento_pago && (
                  <div className="mt-3 text-sm">
                    <span className="text-slate-500">Vence:</span>
                    <span className={`ml-2 font-medium ${purchase.estado_pago === 'vencida' ? 'text-red-700' : ''}`}>
                      {purchase.fecha_vencimiento_pago}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="pt-6 space-y-3">
              {/* Confirm draft */}
              {purchase.es_borrador && (
                <Button
                  onClick={() => setShowConfirmModal(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Compra
                </Button>
              )}

              {/* Pay */}
              {purchase.estado_pago !== 'pagada' && !purchase.es_borrador && (
                <Button
                  onClick={openPaymentModal}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <Banknote className="w-4 h-4 mr-2" />
                  Registrar Pago
                </Button>
              )}

              {/* Mark received */}
              {purchase.estado_recepcion !== 'recibida_completa' && !purchase.es_borrador && (
                <Button
                  variant="outline"
                  onClick={handleMarkReceived}
                  className="w-full"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Marcar como Recibida
                </Button>
              )}

              {/* Back */}
              <Button
                variant="outline"
                onClick={() => navigate('/admin/compras')}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver a Compras
              </Button>
            </CardContent>
          </Card>

          {/* Reception Status */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-slate-500 mb-2">Estado de Recepción</div>
              <Badge variant="outline" className={
                purchase.estado_recepcion === 'recibida_completa' ? 'border-green-300 text-green-700' :
                purchase.estado_recepcion === 'recibida_parcial' ? 'border-amber-300 text-amber-700' :
                'border-slate-300 text-slate-700'
              }>
                {purchase.estado_recepcion_label}
              </Badge>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirm Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Compra</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Al confirmar esta compra se realizarán las siguientes acciones:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                Se registrarán las entradas al inventario
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                Se actualizará el costo promedio de los productos
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                Se creará la cuenta por pagar al proveedor
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>Cancelar</Button>
            <Button onClick={handleConfirm} className="bg-blue-600 hover:bg-blue-700 text-white">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-lg">
                <span>Total a pagar:</span>
                <span className="font-bold text-green-700">{formatCurrency(purchase.total)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monto (Q)</Label>
                <Input
                  type="number"
                  value={paymentData.monto}
                  disabled
                  className="bg-slate-100"
                />
              </div>
              <div>
                <Label>Método</Label>
                <Select 
                  value={paymentData.metodo_pago} 
                  onValueChange={(v) => setPaymentData(prev => ({ ...prev, metodo_pago: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {constants.payment_methods.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha de pago</Label>
                <Input
                  type="date"
                  value={paymentData.fecha_pago}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, fecha_pago: e.target.value }))}
                />
              </div>
              <div>
                <Label>Referencia</Label>
                <Input
                  value={paymentData.referencia}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, referencia: e.target.value }))}
                  placeholder="No. cheque/transfer."
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={paymentData.notas}
                onChange={(e) => setPaymentData(prev => ({ ...prev, notas: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancelar</Button>
            <Button onClick={handlePayment} className="bg-green-600 hover:bg-green-700 text-white">
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseDetailPage;
